import React, { useState, useEffect } from "react";
import Modal from "@components/modals/Modal";
import { useDispatch, useSelector } from "react-redux";
import { closeModal } from "@store/slices/modalSlice";
import { assignToAlllTranslatorAdmins, assignToOneTranslatorAdmin, fetchSelectedFileDataForGAAdmin } from "@store/thunks/gaadminThunk";
import { setAssignToAllAdmins, setShouldRefreshFileInfo, setShouldRefreshFiles } from "@store/slices/gaAdminSlice";
import { BASE_URL } from "@utils/constants";
import { toast } from "react-toastify";
import apiClient from "@/services/api/apiClient";
import { useNavigate } from "react-router-dom";

const AssignTranslatorAdmin = () => {
    const dispatch = useDispatch();
    const isOpen = useSelector((store) => store.modal.assignTranslatorAdmin);
    const selectedFileData = useSelector((store) => store.gaAdmin.selectedFile);
    const fileId = useSelector((store) => store.fileMeta.fileId);
    const [assignedLanguages, setAssignedLanguages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [assigningStates, setAssigningStates] = useState({});
    const { token } = useSelector((store) => store.auth);
    const navigate=useNavigate();

    const handleClose = () => {
        dispatch(closeModal("assignTranslatorAdmin"));
    };

    useEffect(() => {
        if (fileId && (!Array.isArray(selectedFileData) || selectedFileData.length === 0)) {
            setLoading(true);
            dispatch(fetchSelectedFileDataForGAAdmin(fileId))
                .then(() => setLoading(false))
                .catch((error) => {
                    console.error("Error fetching file data:", error);
                    setLoading(false);
                });
        }
    }, [fileId, dispatch]);

    useEffect(() => {
        if (fileId) {
            fetchAssignedLanguages();
        }
    }, [fileId]);

    const fetchAssignedLanguages = async () => {
        try {
            const data = await apiClient(
                BASE_URL + `api/gsAdmin/file/assignedLanguages?fileId=${fileId}`,
                {
                    method: 'GET',
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    }
                },
                navigate
            );
            setAssignedLanguages(data.data || []);
        } catch (error) {
            console.error(error);
            setAssignedLanguages([]);
        }
    };

    const confirmToast = (message, onConfirm) => {
        toast(
            ({ closeToast }) => (
                <div className="p-3">
                    <p className="mb-2 text-gray-800">{message}</p>
                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={() => {
                                onConfirm();
                                closeToast();
                            }}
                            className="px-3 py-1 rounded bg-green-600 text-white text-sm"
                        >
                            Yes
                        </button>
                        <button
                            onClick={closeToast}
                            className="px-3 py-1 rounded bg-gray-400 text-white text-sm"
                        >
                            No
                        </button>
                    </div>
                </div>
            ),
            {
                position: "top-center",
                autoClose: false, // don’t auto-close until user clicks
                closeOnClick: false,
                draggable: false,
                className: "rounded-lg shadow-md border border-gray-200",
            }
        );
    };

    const handleAssign = (row) => {
        confirmToast("Are you sure you want to assign this translator admin?", async () => {
            setAssigningStates((prev) => ({ ...prev, [row.languageId]: true }));
            try {
                await dispatch(assignToOneTranslatorAdmin(fileId, row.userId, row.languageId));
                await fetchAssignedLanguages();
                dispatch(setShouldRefreshFileInfo(true));
                dispatch(setShouldRefreshFiles(true));
                toast.success("Assigned successfully!");
            } catch (error) {
                console.error("Error assigning translator admin:", error);
                toast.error("Failed to assign translator admin");
            } finally {
                setAssigningStates((prev) => ({ ...prev, [row.languageId]: false }));
            }
        });
    };

    const handleAssignAll = () => {
        confirmToast("Are you sure you want to assign to ALL admins?", async () => {
            setLoading(true);
            try {
                const result = await dispatch(assignToAlllTranslatorAdmins(fileId));
                if (result.status === "success") {
                    toast.success("Assigned to all admins successfully!");
                    await fetchAssignedLanguages();
                    dispatch(setAssignToAllAdmins(true));
                    dispatch(setShouldRefreshFileInfo(true));
                    dispatch(setShouldRefreshFiles(true));
                } else {
                    toast.error(result.message || "Failed to assign all");
                }
            } catch (error) {
                console.error("Error assigning to all admins:", error);
                toast.error("Unexpected error while assigning to all admins");
            } finally {
                setLoading(false);
            }
        });
    };

    const safeSelectedFileData = Array.isArray(selectedFileData) ? selectedFileData : [];
    const safeAssignedLanguages = Array.isArray(assignedLanguages) ? assignedLanguages : [];
    const filteredData = safeSelectedFileData
        .filter(item => item.languageName.toLowerCase() !== 'english')
        .filter(item => !safeAssignedLanguages.some(lang => lang.languageId === item.languageId));
    const allAssigned = filteredData.length === 0 && safeSelectedFileData.some(item => item.languageName.toLowerCase() !== 'english');

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title="Assign Translator Admins"
            maxWidth="7xl"
            className="shadow-lg"
        >
            <div className="flex flex-col h-full max-h-[80vh] p-6 sm:p-8">
                <div className="flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="bg-white rounded-lg border border-gray-200 p-6">
                            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
                        </div>
                    ) : allAssigned ? (
                        <div className="bg-green-100 border border-green-400 text-green-700 p-6 rounded-lg font-['Semantic/Strong'] mb-6">
                            <p>File is assigned to all the target languages to a particular admin.</p>
                        </div>
                    ) : filteredData.length === 0 ? (
                        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 p-6 rounded-lg font-['Semantic/Strong']">
                            <p>No available translator admins found.</p>
                            <p className="mt-2 text-sm">
                                All languages are assigned or no translator admins are available. Please contact the administrator if this seems incorrect.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full border border-gray-200 text-sm text-gray-800">
                                <thead className="bg-gray-50 text-gray-700 font-['Semantic/Strong']">
                                    <tr>
                                        <th className="border border-gray-200 px-4 py-2 text-center">Sr. No.</th>
                                        <th className="border border-gray-200 px-4 py-2 text-center">User Name</th>
                                        <th className="border border-gray-200 px-4 py-2 text-center">Status</th>
                                        <th className="border border-gray-200 px-4 py-2 text-center">Language</th>
                                        <th className="border border-gray-200 px-4 py-2 text-center">Assign</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const groupedByUsername = {};
                                        filteredData.forEach((item, index) => {
                                            if (!groupedByUsername[item.userName]) {
                                                groupedByUsername[item.userName] = [];
                                            }
                                            groupedByUsername[item.userName].push({ ...item, originalIndex: index });
                                        });

                                        let srNo = 1;
                                        const rows = [];

                                        Object.keys(groupedByUsername).forEach(username => {
                                            const userItems = groupedByUsername[username];
                                            const rowspan = userItems.length;

                                            userItems.forEach((item, userItemIndex) => {
                                                const isFirstRow = userItemIndex === 0;
                                                rows.push(
                                                    <tr key={`${item.userId}-${item.languageId}`} className="hover:bg-gray-50 transition-colors">
                                                        {isFirstRow && (
                                                            <td className="border border-gray-200 px-4 py-2 text-center font-['Semantic/Strong']" rowSpan={rowspan}>
                                                                {srNo}
                                                            </td>
                                                        )}
                                                        {isFirstRow && (
                                                            <td className="border border-gray-200 px-4 py-2 text-center font-['Semantic/Strong']" rowSpan={rowspan}>
                                                                {item.userName}
                                                            </td>
                                                        )}
                                                        {isFirstRow && (
                                                            <td className="border border-gray-200 px-4 py-2 text-center font-['Semantic/Strong']" rowSpan={rowspan}>
                                                                {item.roleName}
                                                            </td>
                                                        )}
                                                        <td className="border border-gray-200 px-4 py-2 text-center font-['Semantic/Strong']">{item.languageName}</td>
                                                        <td className="border border-gray-200 px-4 py-2 text-center">
                                                            <button
                                                                className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${assigningStates[item.languageId]
                                                                    ? 'border border-gray-200 text-gray-600 cursor-not-allowed'
                                                                    : 'border border-[#0066cc] text-[#0066cc] hover:bg-[#0066cc] hover:text-white focus:ring-2 focus:ring-[#0066cc]/50'
                                                                    }`}
                                                                onClick={() => handleAssign(item)}
                                                                disabled={assigningStates[item.languageId]}
                                                                aria-label={`Assign ${item.languageName} to ${item.userName}`}
                                                            >
                                                                {assigningStates[item.languageId] ? (
                                                                    <div className="flex items-center">
                                                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0066cc] mr-2"></div>
                                                                        Assigning...
                                                                    </div>
                                                                ) : (
                                                                    'Assign'
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            });
                                            srNo++;
                                        });

                                        return rows;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="mt-6">
                        <h2 className="text-lg font-['Semantic/Strong'] text-gray-800 bg-gray-50 border-b border-gray-200 p-2 mb-4">
                            Assigned Languages
                        </h2>
                        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                            {assignedLanguages.length === 0 ? (
                                <p className="text-gray-600 text-sm font-['Semantic/Strong'] p-4 bg-gray-50 rounded-lg">
                                    No languages assigned
                                </p>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {assignedLanguages.map(lang => (
                                        <span
                                            key={lang.languageId}
                                            className="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-['Semantic/Strong'] bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 border border-blue-200 hover:scale-105 hover:shadow-md transition-all duration-200 before:content-['✔'] before:mr-2 before:text-blue-600"
                                        >
                                            {lang.name || 'Unknown Language'}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="sticky bottom-0 bg-white border-t border-gray-200 pt-4 pb-2 flex flex-col sm:flex-row justify-end gap-2">
                    {!allAssigned && filteredData.length > 0 && (
                        <button
                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 ${loading
                                ? 'border border-gray-200 text-gray-600 cursor-not-allowed'
                                : 'border border-[#0066cc] text-[#0066cc] hover:bg-[#0066cc] hover:text-white focus:ring-2 focus:ring-[#0066cc]/50'
                                }`}
                            onClick={handleAssignAll}
                            disabled={loading}
                            aria-label="Assign all languages to translator admins"
                        >
                            {loading ? (
                                <div className="flex items-center">
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0066cc] mr-2"></div>
                                    Assigning All...
                                </div>
                            ) : (
                                'Assign All'
                            )}
                        </button>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default AssignTranslatorAdmin;